import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { Store, Select } from '@ngxs/store';
import { NbDialogService, NbMenuService, NbPopoverDirective } from '@nebular/theme';

// services
import { DeviceService } from '@app/models/devices/devices.service';
import { AppService } from '@app/app.service';
import { LocationService } from '@app/models/locations/locations.service';
import { NotificationService } from '@app/models/notifications/notifications.service';
import { UserService } from '@app/models/users/users.service';

// components
import { LocationSelectorComponent } from '@app/header/location-selector/component';
import { ConfirmComponent } from '@app/shared/confirm/component';
import { DataComponent } from '@app/utils/data.component';

// models
import { Notification } from '@app/models/notifications/notifications.model';
import { Location } from '@app/models/locations/locations.model';

// state
import { AuthState } from '@app/auth/auth.state';
import { ShowSelector, HeaderState, ChangeLocation } from '@app/header/header.state';
import { SetAvailableSpanTypes, SetSelectedSpanTypes } from '@app/state/query-state/query.state';

// utils
import { trackEvent } from '@app/utils/amplitude.utils';

// config
import { config } from '@config/config';

const INSTALLATION_RANK = {
    NOT_STARTED: 0,
    HOME_ADDED: 1,
    BRIDGE_ADDED: 2,
    SENSOR_ADDED: 3,
    BRIDGE_WIFI_CONNECTED: 4,
    DEVICES_CONNECTED: 5,
    METER_ENTERED: 6,
    DONE: 7,
};

export const LOCATION_SORT = (locs, id) => {
    locs.sort((a, b) => {
        if (a.user_id === id && b.user_id !== id) {
            return -1;
        } else if (a.user_id !== id && b.user_id === id) {
            return 1;
        } else if (a.primary_location && !b.primary_location) {
            return -1;
        } else if (!a.primary_location && b.primary_location) {
            return 1;
        } else if (INSTALLATION_RANK[a.installation] > INSTALLATION_RANK[b.installation]) {
            return -1;
        } else if (INSTALLATION_RANK[a.installation] < INSTALLATION_RANK[b.installation]) {
            return 1;
        }
        return 0;
    });
    return locs;
};

@Component({
    selector: 'app-header',
    templateUrl: './template.html',
    styleUrls: ['./style.scss'],
})
export class HeaderComponent extends DataComponent<Notification> implements OnInit, OnDestroy {
    @Select(HeaderState.selectorShown) showSelector$: Observable<boolean>;
    @Select(HeaderState.currentLocation) currentLocation$: Observable<Location>;

    @ViewChild(NbPopoverDirective, { static: false }) popover: NbPopoverDirective;

    intervalRef;
    loggedIn = false;
    name: string;
    unreadNotifications: Notification[] = [];
    unreadNotificationCount: number = 0;
    deviceID: string = null;

    locationList: Observable<Location[]>;

    additionalMenuItems = [
        {
            title: 'Change Location',
            icon: 'home',
        },
        {
            title: 'Find a Local Pro',
            icon: 'car',
        },
        {
            title: 'Settings',
            icon: 'settings',
        },
        {
            title: 'Logout',
            icon: 'close-square',
        },
    ];

    entitlementSubscription: Subscription;
    entitlement: string = null;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private appService: AppService,
        private locations: LocationService,
        private notifs: NotificationService,
        private store: Store,
        private headerState: HeaderState,
        private authState: AuthState,
        private dialogService: NbDialogService,
        private menuService: NbMenuService,
        private device: DeviceService,
        private users: UserService
    ) {
        super({ refresh: 300000 });
    }

    ngOnInit() {
        // subscribe to the user entitlement status
        this.entitlementSubscription = this.users.getUserEntitlement().subscribe((entitlement) => {
            if (entitlement) {
                this.entitlement = entitlement;
            }
        });

        // gets number of unread notifications from subscription to service observable
        this.notifs.unreadNotifications.subscribe((unread) => {
            if (unread) {
                this.unreadNotificationCount = unread.length;
            } else {
                this.unreadNotificationCount = null;
            }
            this.unreadNotifications = unread;
        });

        // listens for clicks in the expanded option menu
        this.attachMenuListeners();
        this.router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                map(() => {
                    let route = this.route;

                    while (route.firstChild) {
                        route = route.firstChild;
                    }

                    return route;
                }),
                filter((route) => route.outlet === 'primary'),
                mergeMap((route) => route.data)
            )
            .subscribe((route) => {
                this.store.dispatch(new ShowSelector(!!route.locationSelector));
                // closes the notifications popover after navigating to a notification
                this.popover.hide();
            });

        // gets the available location options for the user
        this.authState.emitAuthComplete().subscribe(() => {
            this.loggedIn = true;

            this.locationList = this.locations.getAll({ list_shared: true, profile: true }).pipe(
                switchMap((res) => this.authState.getUser().pipe(map((user) => ({ id: user.id, res })))),
                map((data) => LOCATION_SORT(data.res, data.id))
            );

            this.locationList.subscribe((locs) => {
                const storedLoc = localStorage.getItem('location');
                let location;
                if (storedLoc) {
                    const loc = locs.filter((l) => l.id === Number(storedLoc));
                    location = loc[0] || locs[0];
                } else {
                    location = locs[0];
                }
                this.store.dispatch(new ChangeLocation(location));
                // extract the span types in the usage profile and add to state
                this.addActiveAppliancesToState(location);
            });
        });

        this.authState.emitAuthInvalid().subscribe(() => localStorage.removeItem('location'));

        this.headerState.onChangeLocation().subscribe(() => {
            this.getUnreadNotificationCount(this.store.selectSnapshot(HeaderState.currentLocation).id);
            this.appService.reloadData();
        });
    }

    ngOnDestroy() {
        if (this.entitlementSubscription) {
            this.entitlementSubscription.unsubscribe();
        }
    }

    getUnreadNotificationCount(id) {
        // this.getUnreadNotifications(id);
        this.notifs.getUnreadNotifications(id);
    }

    getUnreadNotifications(id) {
        this.notifs
            .getAll({ location_id: id, read: false })
            .pipe()
            .subscribe((data) => {
                this.unreadNotifications.push(...data);
                this.unreadNotificationCount = data.length;
            });
    }

    reduceUnreadCount() {
        if (this.unreadNotificationCount > 0) {
            this.unreadNotificationCount -= 1;
        }
    }

    resetUnreadCount() {
        this.unreadNotificationCount = 0;
    }

    hidePopover() {
        this.popover.hide();
    }

    goToCurrent() {
        this.router.navigate(['/dashboard']);
    }

    chooseLocation() {
        this.dialogService
            .open(LocationSelectorComponent, {
                context: {
                    userID: this.authState.userID(),
                    activeLoc: this.store.selectSnapshot(HeaderState.currentLocation).id,
                    LOCATION_SORT,
                },
            })
            .onClose.subscribe((loc) => {
                if (loc) {
                    trackEvent('Header Location Changed', { id: loc.id, name: loc.name });
                    this.store.dispatch(new ChangeLocation(loc));
                    this.addActiveAppliancesToState(loc);
                }
            });
    }

    confirmLogout() {
        this.dialogService
            .open(ConfirmComponent, {
                context: {
                    title: `Confirm Logout`,
                    content: 'Are you sure you want to logout?',
                    confirmText: 'Yes, logout',
                    confirmStatus: 'danger',
                },
            })
            .onClose.subscribe((logout) => {
                if (logout) {
                    this.router.navigate(['/logout']);
                }
            });
    }

    attachMenuListeners() {
        this.menuService
            .onItemClick()
            .pipe(
                filter(({ tag }) => tag === 'add-menu'),
                map(({ item: { icon } }) => icon)
            )
            .subscribe((item) => {
                switch (item) {
                    case 'close-square':
                        this.confirmLogout();
                        break;
                    case 'home':
                        this.chooseLocation();
                        break;
                    case 'settings':
                        this.router.navigate(['/settings']);
                        break;
                    case 'car':
                        this.router.navigate(['/local-pro']);
                        break;
                    default:
                        this.router.navigate(['/dashboard']);
                        break;
                }
            });
    }

    // compare the appliances in the usage profile with the appliances that are active in disagg and add to state
    addActiveAppliancesToState(location) {
        // if not using disagg features return default list of appliances
        if (!location.features.use_profile_for_disag || !location.profile) {
            // set the default list of appliances, starting with all
            const defaultAppliances = Object.keys(config.activeSpanTypes).reduce(
                (acc, spanType) => {
                    if (config.activeSpanTypes[spanType] === true) {
                        acc.push({
                            field: spanType,
                            display: this.capitalize(spanType),
                            value: true,
                        });
                    }
                    return acc;
                },
                [
                    {
                        field: 'all',
                        display: 'All Usage',
                        value: true,
                    },
                ]
            );
            this.store.dispatch(new SetSelectedSpanTypes(['all']));
            this.store.dispatch(new SetAvailableSpanTypes(defaultAppliances));
        } else {
            const indoorAppliances = location.profile.indoor.reduce((acc, curr) => {
                let temp = {};
                if (curr.field === 'water_softener') {
                    temp = {
                        field: 'softener',
                        display: 'Water Softener',
                        value: curr.value,
                    };
                } else {
                    temp = {
                        field: curr.field,
                        display: curr.display,
                        value: curr.value,
                    };
                }
                acc.push(temp);
                return acc;
            }, []);
            // add default indoor and outdoor types if they are present
            const indoor = this.checkIndoorOutdoor(indoorAppliances);
            const outdoor = this.checkIndoorOutdoor(location.profile.outdoor);
            const selectedAppliances = [
                {
                    field: 'all',
                    display: 'All Usage',
                    value: true,
                },
            ];
            if (outdoor) {
                selectedAppliances.push({
                    field: 'outdoor',
                    display: 'Outdoor',
                    value: true,
                });
            }
            if (indoor) {
                selectedAppliances.push({
                    field: 'indoor',
                    display: 'Indoor',
                    value: true,
                });
            }
            // add available additional appliances
            const activeSpanTypes = config.activeSpanTypes;
            selectedAppliances.push(
                ...[...indoorAppliances, ...location.profile.outdoor].filter(
                    (spanType) => activeSpanTypes[spanType.field] === true && spanType.value === true
                )
            );
            // set to state
            this.store.dispatch(new SetSelectedSpanTypes(['all']));
            this.store.dispatch(new SetAvailableSpanTypes(selectedAppliances));
        }
    }

    // check that at least one span type is true
    checkIndoorOutdoor(spanTypes) {
        return spanTypes.reduce((acc, curr) => {
            if (curr.value === true) {
                acc = true;
            }
            return acc;
        }, false);
    }

    // capitalize string and remove underscores
    capitalize(str) {
        return str
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }
}
